// The Yearly Summaries as a PDF (#515 stage 2): the tab's table four years at a time, the contract's
// totals, and the Scope PA grid with the Payments per Annum — drawn with the Schedule of Work's kit
// (schedule/pdf-kit.ts) and saved to the job's SharePoint folder like it. jsPDF loads only when made.
import { CONTRACT_CATEGORIES, CONTRACT_CATEGORY_LABELS, FILE_NAME_MAX, fitFileName, type ContractCategory, type YearWork } from '@higgins/m2q-pricing';
import type { MaintenancePricing } from './maintenance-pricing';
import { embedRoboto } from './schedule/pdf-fonts';
import { PdfPage, table, type Column } from './schedule/pdf-kit';

const CATEGORY_LABEL = CONTRACT_CATEGORY_LABELS;
/** The year tables' narrower label column: the same rows, the long one shortened. */
const SHORT_LABEL: Record<ContractCategory, string> = { ...CATEGORY_LABEL, prelims: 'Prelims (minor contr.)' };
const WORK_LETTER: Record<YearWork, string> = { repaint: 'R', service: 'M', wash: 'W', none: '–' };
/** Years per table on portrait A4: four sell/cost pairs beside the row labels. */
const YEARS_PER_TABLE = 4;
/** Years per row of the Scope PA grid. */
const SCOPE_YEARS_PER_TABLE = 8;
const LABEL_WIDTH = 42;
const MONEY_WIDTH = 17;
const SCOPE_LABEL_WIDTH = 54;
const SCOPE_YEAR_WIDTH = 16;
const EXTERIOR_SIDE = 'External';
const PERCENT = 100;
/** Characters SharePoint won't take in a file name. */
const NOT_IN_FILE_NAMES = /["*:<>?/\\|]/g;

const money = (value: number) => value.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const chunks = <T>(items: readonly T[], size: number): T[][] => Array.from({ length: Math.ceil(items.length / size) }, (_, i) => items.slice(i * size, i * size + size));

export interface YearlyPdfInput {
  jobNumber: string;
  optionName: string;
  pricing: MaintenancePricing;
  /** Each area's side, by area id (Interior, then Exterior, as the template's Scope sheet). */
  sides: ReadonlyMap<string, string | null>;
  preparedBy: string | null;
  /** The date shown in the footer (dd/mm/yyyy). */
  date: string;
}

/**
 * "VRT003 Yearly Summaries B – 5-year maintenance v1 2026-10-23.pdf". A long option name is
 * shortened (tagged, so two options never share a file), never the version or the date.
 */
export function yearlyFileName(jobNumber: string, optionName: string, versionNumber: number | null, isoDate: string): string {
  const safe = (text: string) => text.replace(NOT_IN_FILE_NAMES, '-');
  const end = `${versionNumber ? ` v${versionNumber}` : ''} ${isoDate}.pdf`;
  return fitFileName([`${safe(jobNumber)} Yearly Summaries `, { shortenable: safe(optionName) }, end], FILE_NAME_MAX, { tagged: true });
}

function yearTables(page: PdfPage, pricing: MaintenancePricing) {
  const { yearly } = pricing;
  for (const years of chunks(yearly.years, YEARS_PER_TABLE)) {
    const columns: Column[] = [{ label: 'Items/Descriptions', width: LABEL_WIDTH }];
    for (const y of years) columns.push({ label: `${y.label} Sell`, width: MONEY_WIDTH, align: 'right' }, { label: 'Cost', width: MONEY_WIDTH, align: 'right' });
    const pair = (sell: (y: (typeof years)[number]) => number, cost: (y: (typeof years)[number]) => number | null) =>
      years.flatMap((y) => [money(sell(y)), cost(y) === null ? '' : money(cost(y) as number)]);
    table(page, columns, [
      ...CONTRACT_CATEGORIES.map((c) => ({ cells: [SHORT_LABEL[c], ...pair((y) => y.categories[c].sell, (y) => y.categories[c].cost)] })),
      { cells: ['Interest', ...pair((y) => y.interest, () => null)] },
      { cells: ['Totals', ...pair((y) => y.sell, (y) => y.cost)], bold: true, shade: true },
      { cells: ['Hours · Litres', ...years.flatMap((y) => [y.hours.toFixed(2), y.litres.toFixed(2)])] },
    ]);
  }
}

function totalsTable(page: PdfPage, pricing: MaintenancePricing) {
  const { yearly } = pricing;
  const columns: Column[] = [
    { label: 'Contract totals', width: LABEL_WIDTH + MONEY_WIDTH * 2 },
    { label: 'Sell', width: MONEY_WIDTH * 2, align: 'right' },
    { label: 'Cost', width: MONEY_WIDTH * 2, align: 'right' },
  ];
  table(page, columns, [
    ...CONTRACT_CATEGORIES.map((c) => ({ cells: [CATEGORY_LABEL[c], money(yearly.totals[c].sell), money(yearly.totals[c].cost)] })),
    { cells: ['Interest', money(yearly.interest), ''] },
    { cells: ['Totals', money(yearly.sell), money(yearly.cost)], bold: true, shade: true },
    { cells: ['GP %', `${(yearly.gp * PERCENT).toFixed(1)}%`, ''] },
  ]);
}

function scopeTables(page: PdfPage, pricing: MaintenancePricing, sides: ReadonlyMap<string, string | null>) {
  const { contract, yearly } = pricing;
  const groups = [
    { label: 'Interior', areas: contract.sections.filter((s) => sides.get(s.id) !== EXTERIOR_SIDE) },
    { label: 'Exterior', areas: contract.sections.filter((s) => sides.get(s.id) === EXTERIOR_SIDE) },
  ].filter((g) => g.areas.length);
  chunks(yearly.years, SCOPE_YEARS_PER_TABLE).forEach((years, chunk) => {
    const first = chunk * SCOPE_YEARS_PER_TABLE;
    const columns: Column[] = [{ label: 'Area', width: SCOPE_LABEL_WIDTH }, ...years.map((y) => ({ label: `${y.label} (${y.number})`, width: SCOPE_YEAR_WIDTH, align: 'right' as const }))];
    table(page, columns, [
      ...groups.flatMap((g) => [
        { cells: [g.label, ...years.map(() => '')], bold: true, shade: true },
        ...g.areas.map((s) => ({ cells: [s.name, ...years.map((_, i) => WORK_LETTER[s.programme[first + i] ?? 'none'])] })),
      ]),
      { cells: ['Payments per Annum', ...years.map((y) => money(y.sell))], bold: true },
    ]);
  });
}

/** The PDF itself. */
export async function drawYearlySummaries(input: YearlyPdfInput): Promise<Blob> {
  const { jobNumber, optionName, pricing, sides, preparedBy, date } = input;
  const { jsPDF } = await import('jspdf');
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4', compress: true });
  await embedRoboto(doc);
  doc.setProperties({ title: `${jobNumber} Yearly Summaries`, subject: optionName, author: preparedBy ?? 'Higgins Coatings', creator: 'Measure2Quote' });
  const page = new PdfPage(doc, `${jobNumber} · ${optionName}`, 'Yearly Summaries');
  page.drawHeader();
  page.paragraph(`${jobNumber} — Yearly Maintenance Breakdown`, { size: 14, style: 'bold' });
  const terms = [`${pricing.years} years from ${pricing.startYear}`, `Maintenance Type: ${pricing.yearly.paymentType}`, pricing.tier ? `Tier: ${pricing.tier}` : null];
  page.paragraph(`${optionName} · ${terms.filter(Boolean).join(' · ')}`, { size: 9.5, gapAfter: 4 });
  page.bar('Yearly Summaries');
  yearTables(page, pricing);
  totalsTable(page, pricing);
  page.bar('Scope PA — Schedule of Work');
  scopeTables(page, pricing, sides);
  page.paragraph('R Repaint · M Service · W Wash · – No Work. Payments per Annum are the Yearly Summaries’ totals.', { size: 8 });
  page.drawFooters(date);
  return doc.output('blob');
}
